import React, { useMemo, useState, useEffect } from "react";
import {
  Form,
  Select,
  Input,
  Modal,
  message,
  Button,
  Radio,
  Typography,
  Empty,
} from "antd";

import {
  WhatsAppOutlined,
  MailOutlined,
  UserOutlined,
  TeamOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import userService from "../../../services/UserService";
import { useUser } from "../../../context/UserContext";

import "dayjs/locale/es";

import PageHeader from "../../headers/PageHeader";
import notificacionesService from "../../../services/notificacionesService";

const { Text } = Typography;

const CHANNELS = [
  {
    key: "whatsapp",
    label: "WhatsApp",
    caption: "Mensaje directo al celular",
    icon: <WhatsAppOutlined style={{ fontSize: 20, color: "#25D366" }} />,
    iconBg: "#E9F9EF",
  },
  {
    key: "email",
    label: "Correo",
    caption: "Enviado a la bandeja de entrada",
    icon: <MailOutlined style={{ fontSize: 20, color: "#3B82F6" }} />,
    iconBg: "#EFF3FF",
  },
];

const ROLES = [
  { label: "Inspector", value: "CISEInspector" },
  { label: "Analista", value: "CISEAnalyts" },
  { label: "Administrador", value: "CISEAdministrador" },
  { label: "Coordinador", value: "Coordinator" },
];

const SendWoT = ({ open, setOpen, record }) => {
  const user = useUser();
  const [form] = Form.useForm();

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [channel, setChannel] = useState(null);
  const [recipientMode, setRecipientMode] = useState("custom");

  const additionalNote = Form.useWatch("messageContentAditional", form);

  const fetchData = async () => {
    setLoadingUsers(true);
    try {
      const usuariosData = await userService.consultarUsuarios(user.idToken);

      const mappedUsersData = (usuariosData.Items || [])
        .filter(
          (u) =>
            u.role !== "CISEAdministrador" &&
            u.name !== "<empty>" &&
            // Usuarios sin registro completo: quedan con nombre y apellido
            // "Pendiente" y no deben aparecer como destinatarios.
            !(u.name?.trim() === "Pendiente" && u.lastName?.trim() === "Pendiente"),
        )
        .map((u) => ({
          id: u.id,
          label: `${u.name} ${u.lastName}`,
          phone: u.phone || "",
          email: u.email || "",
        }));
      setUsers(mappedUsersData);
    } catch (error) {
      console.error("Error al obtener usuarios:", error);
      message.error("No se pudo cargar la lista de usuarios.");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
    } else {
      form.resetFields();
      setChannel(null);
      setRecipientMode("custom");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // El Select usa el `id` del usuario como value (único de verdad). Antes se
  // usaba "telefono/nombre completo": con usuarios sin teléfono registrado
  // varios terminaban con el mismo value, y Ant Design identifica las
  // opciones por value, así que la búsqueda/selección mezclaba usuarios
  // distintos entre sí.
  const userOptions = useMemo(
    () => users.map(({ id, label }) => ({ label, value: id })),
    [users]
  );

  const usersById = useMemo(
    () =>
      users.reduce((acc, u) => {
        acc[u.id] = u;
        return acc;
      }, {}),
    [users]
  );

  const formatDate = (date) =>
    date ? dayjs(date).format("DD/MM/YYYY hh:mm") : "Sin fecha";

  const ubicacion = record
    ? `${record.location.specific}-${record.location.name} Zona:${record.location.zone}`
    : "";

  const preview = useMemo(() => {
    if (!record) return "";
    const cuando = dayjs(record.createdAt)
      .locale("es")
      .format("dddd, D [de] MMMM [de] YYYY [a las] HH:mm [horas]");
    const nota = additionalNote
      ? `\n📝 Nota adicional:\n${additionalNote}`
      : "";

    if (channel === "email") {
      return `Asunto: Actualización del evento ${record.id}

Hola,

Se registró una actualización en la plataforma CISE:

✅ Evento: ${record.id}
✅ Protocolos: ${(record.protocols || []).join(", ")}
✅ Fecha y hora: ${cuando}
✅ Ubicación: ${ubicacion}${nota}

Ingresa a la plataforma CISE para hacer seguimiento.`;
    }

    return `✅ ¿Qué?
El evento: ${record.id}
✅ ¿Quién?
Protocolos asociados: ${(record.protocols || []).join(", ")}
✅ ¿Cuándo?
${cuando}
✅ ¿Dónde?
${ubicacion}${nota}

✅ Acciones realizadas desde el CISE.`;
  }, [record, channel, additionalNote, ubicacion]);

  const enviarRegistro = async (registro) => {
    try {
      const enviar =
        channel === "email"
          ? notificacionesService.EnviarNotificacion
          : notificacionesService.EnviarNotificacionWP;
      await enviar(registro);
    } catch (error) {
      console.error("Error al enviar notificación:", error);
      throw error;
    }
  };

  const handleFormSubmit = async (values) => {
    setSubmitting(true);
    const arregloP = record.protocols.join(",");

    const buildBase = () => ({
      eventId: record.id,
      que: "El evento: " + record.id,
      quien: `Protocolos asociados: ${arregloP}`,
      cuando: formatDate(record.stateHistory.Open.date),
      donde: ubicacion,
      como: record.title.replace(/\n/g, ""),
      observacion: values?.messageContentAditional || " ",
    });

    const envios =
      recipientMode === "custom"
        ? values.phoneNumber.map((userId) => {
            const destinatario = usersById[userId] || {};
            return enviarRegistro({
              ...buildBase(),
              phone: destinatario.phone || "",
              nombre: destinatario.label || "",
              // Envío individual por correo: el backend necesita la dirección
              // del destinatario seleccionado (por rol la resuelve él mismo).
              ...(channel === "email"
                ? { correo: destinatario.email || "" }
                : {}),
            });
          })
        : values.roles.map((roleValue) =>
            enviarRegistro({
              ...buildBase(),
              phone: "",
              role: roleValue,
            }),
          );

    try {
      await Promise.all(envios);
      setOpen(false);
      message.success("Notificación enviada");
    } catch (error) {
      console.error("Error al enviar notificación:", error);
      message.error("No se logró hacer la solicitud.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title=""
      centered
      open={open}
      onCancel={() => setOpen(false)}
      width={640}
      footer={false}
      destroyOnClose
    >
      <PageHeader title="Enviar notificación" head="Notificaciones" />

      <div style={{ padding: "0 16px 16px" }}>
        <Eyebrow text="Paso 1 · Canal" />
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          {CHANNELS.map((c) => {
            const selected = channel === c.key;
            return (
              <div
                key={c.key}
                role="button"
                tabIndex={0}
                onClick={() => setChannel(c.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setChannel(c.key);
                }}
                style={{
                  flex: 1,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  borderRadius: 10,
                  cursor: "pointer",
                  border: selected
                    ? "1.5px solid #7AC142"
                    : "1px solid #E5E7EB",
                  background: selected ? "rgba(122,193,66,0.06)" : "#fff",
                  transition: "border-color .15s ease, background .15s ease",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: c.iconBg,
                    flexShrink: 0,
                  }}
                >
                  {c.icon}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.label}</div>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {c.caption}
                  </div>
                </div>
                {selected && (
                  <CheckCircleFilled
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      color: "#7AC142",
                      fontSize: 16,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {channel && (
          <Form form={form} onFinish={handleFormSubmit} layout="vertical">
            <Eyebrow text="Paso 2 · Destinatarios" />
            <Form.Item style={{ marginBottom: 12 }}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={recipientMode}
                onChange={(e) => setRecipientMode(e.target.value)}
              >
                <Radio.Button value="custom">
                  <UserOutlined /> Usuarios específicos
                </Radio.Button>
                <Radio.Button value="role">
                  <TeamOutlined /> Por rol
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            {recipientMode === "role" && (
              <Form.Item
                name="roles"
                rules={[
                  { required: true, message: "Selecciona al menos un rol" },
                ]}
                extra="Se enviará una notificación individual a cada persona con el rol seleccionado."
              >
                <Select
                  mode="multiple"
                  placeholder="Selecciona uno o más roles"
                  options={ROLES}
                  optionFilterProp="label"
                  maxTagCount="responsive"
                />
              </Form.Item>
            )}

            {recipientMode === "custom" && (
              <Form.Item
                name="phoneNumber"
                rules={[
                  { required: true, message: "Selecciona al menos un usuario" },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Busca y selecciona usuarios"
                  loading={loadingUsers}
                  options={userOptions}
                  optionFilterProp="label"
                  showSearch
                  maxTagCount="responsive"
                  notFoundContent={
                    loadingUsers ? (
                      "Cargando usuarios…"
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No hay usuarios disponibles"
                      />
                    )
                  }
                />
              </Form.Item>
            )}

            <Eyebrow text="Paso 3 · Mensaje" />
            <div
              style={{
                border: "1px solid #E5E7EB",
                borderRadius: 10,
                padding: "12px 14px",
                background: "#FAFAFA",
                marginBottom: 12,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                Vista previa{" "}
                {channel === "email" ? "del correo" : "del mensaje"} — se genera
                automáticamente a partir del evento
              </Text>
              <pre
                style={{
                  margin: "8px 0 0",
                  fontFamily: "inherit",
                  fontSize: 13,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {preview}
              </pre>
            </div>

            <Form.Item
              label="Nota adicional (opcional)"
              name="messageContentAditional"
            >
              <Input.TextArea
                rows={2}
                placeholder="Agrega contexto adicional para el destinatario"
              />
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={submitting}>
              Enviar notificación
            </Button>
          </Form>
        )}
      </div>
    </Modal>
  );
};

const Eyebrow = ({ text }) => (
  <Text
    type="secondary"
    style={{
      display: "block",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 8,
    }}
  >
    {text}
  </Text>
);

export default SendWoT;
